import { Component } from 'react'
import * as Sentry from '@sentry/react'
import { JAI_COLORS, JAI_FONTS } from './JaiBrand'

/**
 * ErrorBoundary — catches render crashes from any child page so one bad page
 * shows a friendly "something went wrong" panel instead of a blank white screen.
 *
 * Must be a class component: only class components can use componentDidCatch /
 * getDerivedStateFromError, React's only hooks for trapping render errors.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false, eventId: null }
  }

  // Runs when a child throws during render — flips us into the fallback view.
  static getDerivedStateFromError() {
    return { hasError: true }
  }

  // Runs alongside the above. Logs locally, then reports the crash to Sentry
  // (a no-op when no DSN is configured, e.g. local dev). React swallows the
  // error once a boundary catches it, so without this call a crashed page is
  // completely invisible to us — the user just sees the panel below and we
  // never hear about it.
  componentDidCatch(error, info) {
    console.error('Page crashed:', error, info)
    const eventId = Sentry.captureException(error, {
      contexts: { react: { componentStack: info?.componentStack } },
    })
    this.setState({ eventId })
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: 'calc(100vh / var(--jai-zoom))',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 24,
            textAlign: 'center',
            background: JAI_COLORS.paper,
            fontFamily: JAI_FONTS.tight,
            color: JAI_COLORS.ink,
          }}
        >
          <div style={{ fontSize: 22, fontWeight: 600 }}>Something went wrong</div>
          <div style={{ color: JAI_COLORS.mute, maxWidth: 420 }}>
            This page hit an unexpected snag. Your data is safe — try reloading,
            or head back to your chat.
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '10px 18px',
                borderRadius: 10,
                border: 'none',
                cursor: 'pointer',
                background: JAI_COLORS.blue,
                color: JAI_COLORS.white,
                fontFamily: JAI_FONTS.tight,
                fontWeight: 600,
              }}
            >
              Reload page
            </button>
            <button
              onClick={() => { window.location.href = '/chat' }}
              style={{
                padding: '10px 18px',
                borderRadius: 10,
                border: `1px solid ${JAI_COLORS.hair}`,
                cursor: 'pointer',
                background: JAI_COLORS.white,
                color: JAI_COLORS.ink,
                fontFamily: JAI_FONTS.tight,
                fontWeight: 600,
              }}
            >
              Back to chat
            </button>
          </div>
          {this.state.eventId && (
            // Quoting this in an email to support turns "a page broke" into a
            // one-click lookup of the exact crash in Sentry.
            <div
              style={{
                marginTop: 4,
                fontSize: 11,
                color: JAI_COLORS.mute,
                fontFamily: JAI_FONTS.mono,
              }}
            >
              Reference: {this.state.eventId.slice(0, 8)}
            </div>
          )}
        </div>
      )
    }

    return this.props.children
  }
}
